import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshCw, CheckCircle, X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AppLayout } from "../../../components/layout";
import { ContentCard, PageHeader } from "../../../components/ui";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { inventoryManagementService } from "../../../services/inventoryManagementService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../../components/ui/dialog";
import { cn } from "../../../lib/utils";

const ALERT_STATUS_KEYS = {
  New: "status.new",
  Acknowledged: "status.acknowledged",
  Snoozed: "status.snoozed",
};

export function ReorderAlertsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [successToast, setSuccessToast] = useState("");

  // Alerts data
  const [alerts, setAlerts] = useState([]);

  // Filters State
  const [filters, setFilters] = useState({
    productId: "All",
    warehouse: "All",
    status: "All",
    category: "All",
  });

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  // Dialog triggers
  const [ackDialogOpen, setAckDialogOpen] = useState(false);
  const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);
  const [activeAlert, setActiveAlert] = useState(null);

  // Snooze form fields
  const [snoozeDays, setSnoozeDays] = useState(7);
  const [snoozeNotes, setSnoozeNotes] = useState("");
  const [snoozeError, setSnoozeError] = useState("");

  // Load alerts and options lists
  const loadAlerts = useCallback((isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    inventoryManagementService.getReorderAlerts(filters)
      .then((res) => {
        setAlerts(res);
        setPage(1);
        setLoading(false);
        setRefreshing(false);
      })
      .catch((err) => {
        console.error("Failed to load reorder alerts:", err);
        setLoading(false);
        setRefreshing(false);
      });
  }, [filters]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const handleResetFilters = () => {
    setFilters({
      productId: "All",
      warehouse: "All",
      status: "All",
      category: "All",
    });
  };

  // Helper lists for filters
  const filterOptions = useMemo(() => {
    const productsMap = {};
    const warehousesSet = new Set();
    const categoriesSet = new Set();

    alerts.forEach((a) => {
      productsMap[a.productId] = a.productName;
      if (a.warehouse) warehousesSet.add(a.warehouse);
      if (a.category) categoriesSet.add(a.category);
    });

    return {
      products: Object.entries(productsMap).map(([id, name]) => ({ id, name })),
      warehouses: Array.from(warehousesSet),
      categories: Array.from(categoriesSet),
    };
  }, [alerts]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.productId !== "All") count++;
    if (filters.warehouse !== "All") count++;
    if (filters.status !== "All") count++;
    if (filters.category !== "All") count++;
    return count;
  }, [filters]);

  // Scoped paginated alerts
  const paginatedAlerts = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return alerts.slice(start, start + PAGE_SIZE);
  }, [alerts, page]);

  const totalPages = Math.max(1, Math.ceil(alerts.length / PAGE_SIZE));

  // Acknowledge handler
  const triggerAcknowledge = (alertObj) => {
    setActiveAlert(alertObj);
    setAckDialogOpen(true);
  };

  const handleAcknowledgeConfirm = () => {
    if (!activeAlert) return;
    inventoryManagementService.acknowledgeReorderAlert(activeAlert.id)
      .then(() => {
        setSuccessToast(t("inventory.alertAcknowledgedSuccess"));
        setAckDialogOpen(false);
        loadAlerts(true);
        setTimeout(() => setSuccessToast(""), 3000);
      })
      .catch((err) => {
        console.error("Failed to acknowledge alert:", err);
      });
  };

  // Snooze handler
  const triggerSnooze = (alertObj) => {
    setActiveAlert(alertObj);
    setSnoozeDays(7);
    setSnoozeNotes("");
    setSnoozeError("");
    setSnoozeDialogOpen(true);
  };

  const handleSnoozeConfirm = (e) => {
    e.preventDefault();
    if (snoozeDays < 1 || snoozeDays > 30) {
      setSnoozeError(t("dateTime.snoozeDaysValidation"));
      return;
    }

    if (!activeAlert) return;
    inventoryManagementService.snoozeReorderAlert({
      id: activeAlert.id,
      days: snoozeDays,
      notes: snoozeNotes,
    })
      .then(() => {
        setSuccessToast(t("inventory.alertSnoozedSuccess"));
        setSnoozeDialogOpen(false);
        loadAlerts(true);
        setTimeout(() => setSuccessToast(""), 3000);
      })
      .catch((err) => {
        console.error("Failed to snooze alert:", err);
      });
  };

  // Status Badge Class resolver
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "New":
        return "bg-rose-50 text-rose-700 border-rose-200";
      case "Acknowledged":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "Snoozed":
        return "bg-amber-50 text-amber-700 border-amber-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return "—";
    return new Date(isoStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <AppLayout
      breadcrumb={[
        { label: t("inventory.operations") },
        { label: t("inventory.inventoryManagement"), onClick: () => navigate("/inventory-management/stock-position") },
        { label: t("inventory.reorderAlerts") },
      ]}
      className="space-y-6 animate-fade-in"
    >
      {/* Toast Alert Success popup */}
      {successToast && (
        <div className="fixed top-5 right-5 z-50 bg-emerald-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-in fade-in duration-300 font-sans font-bold text-xs">
          <span>✔️</span>
          <span>{successToast}</span>
        </div>
      )}

      {/* Acknowledge Alert Confirmation Primitive Dialog */}
      <Dialog open={ackDialogOpen} onOpenChange={setAckDialogOpen}>
        <DialogPortal>
          <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-sm fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
              "w-[90vw] sm:w-[380px]",
              "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
              "p-6 flex flex-col gap-4 font-sans focus:outline-none",
              "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
            )}
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-[var(--color-primary)]" />
              <DialogTitle className="text-sm font-bold text-foreground">
                {t("inventory.acknowledgeTitle")}
              </DialogTitle>
            </div>
            <p className="text-xs text-[var(--color-text-secondary)] font-semibold leading-relaxed">
              {t("inventory.acknowledgeBody")}
            </p>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setAckDialogOpen(false)}
                className="h-9 px-4 rounded-lg border border-[var(--color-text-border)] text-[var(--color-text-secondary)] font-bold text-xs hover:bg-gray-50 cursor-pointer"
              >
                {t("formActions.cancel")}
              </button>
              <button
                type="button"
                onClick={handleAcknowledgeConfirm}
                className="h-9 px-4 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-accent)] text-white font-bold text-xs shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer"
              >
                {t("inventory.acknowledge")}
              </button>
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      {/* Snooze Alert Dialog Modal (Matches UI Mockup) */}
      <Dialog open={snoozeDialogOpen} onOpenChange={setSnoozeDialogOpen}>
        <DialogPortal>
          <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-sm fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
              "w-[90vw] sm:w-[420px]",
              "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
              "p-6 flex flex-col gap-4 font-sans focus:outline-none",
              "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
            )}
          >
            <div className="flex items-center justify-between">
              <DialogTitle className="text-base font-extrabold text-foreground tracking-tight">
                {t("formActions.snoozeAlert")}
              </DialogTitle>
              <DialogPrimitive.Close className="h-7 w-7 grid place-items-center rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)] hover:text-foreground transition cursor-pointer">
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>

            <p className="text-[11px] text-[var(--color-text-secondary)] font-semibold leading-relaxed">
              {t("formActions.snoozeBody")}
              {activeAlert?.productName ? (
                <>
                  {" "}
                  <strong className="text-foreground">{activeAlert.productName}</strong>
                </>
              ) : null}
            </p>

            <form onSubmit={handleSnoozeConfirm} className="space-y-4 pt-1">
              <div className="space-y-1.5">
                <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400">
                  {t("dateTime.snoozeForDays")}
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={snoozeDays}
                  onChange={(e) => {
                    setSnoozeDays(Number(e.target.value) || 0);
                    if (snoozeError) setSnoozeError("");
                  }}
                  className={cn(
                    "w-[120px] h-9 px-3 rounded-lg border text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none",
                    snoozeError ? "border-rose-500" : "border-[var(--color-text-border)]"
                  )}
                />
                {snoozeError && (
                  <p className="text-[10px] text-rose-500 font-bold mt-1">⚠️ {snoozeError}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400">
                  {t("formActions.snoozeNotes")}
                </label>
                <textarea
                  value={snoozeNotes}
                  onChange={(e) => setSnoozeNotes(e.target.value)}
                  placeholder={t("formActions.snoozeNotesPlaceholder")}
                  className="w-full h-16 p-2 rounded-lg border border-[var(--color-text-border)] text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setSnoozeDialogOpen(false)}
                  className="h-9 px-4 rounded-lg border border-[var(--color-text-border)] text-[var(--color-text-secondary)] font-bold text-xs hover:bg-gray-50 cursor-pointer"
                >
                  {t("formActions.cancel")}
                </button>
                <button
                  type="submit"
                  className="h-9 px-5 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-accent)] text-white font-bold text-xs shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer"
                >
                  {t("formActions.snoozeAlert")}
                </button>
              </div>
            </form>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      <PageHeader
        title={t("inventory.reorderAlerts")}
        action={
          <button
            type="button"
            onClick={() => loadAlerts(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[var(--color-text-border)] bg-white text-xs font-bold text-foreground hover:bg-[var(--color-workspace)]/40 hover:text-[var(--color-primary)] transition cursor-pointer disabled:opacity-50"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", refreshing && "animate-spin")} />
            <span>{t("formActions.refresh")}</span>
          </button>
        }
      />

      {/* Filter panel */}
      <ContentCard title={t("formActions.filters")} bodyClassName="p-5 font-sans">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          {/* Status Dropdown */}
          <div className="space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
              {t("status.status")}
            </span>
            <Select
              value={filters.status}
              onValueChange={(val) => setFilters((prev) => ({ ...prev, status: val }))}
            >
              <SelectTrigger className="h-9 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full">
                <SelectValue placeholder={t("inventory.allStatuses")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">{t("inventory.allStatuses")}</SelectItem>
                <SelectItem value="New">{t("status.new")}</SelectItem>
                <SelectItem value="Acknowledged">{t("status.acknowledged")}</SelectItem>
                <SelectItem value="Snoozed">{t("status.snoozed")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Category Dropdown */}
          <div className="space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
              {t("common.category")}
            </span>
            <Select
              value={filters.category}
              onValueChange={(val) => setFilters((prev) => ({ ...prev, category: val }))}
            >
              <SelectTrigger className="h-9 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full">
                <SelectValue placeholder={t("inventory.allCategories")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">{t("inventory.allCategories")}</SelectItem>
                {filterOptions.categories.map((cName, idx) => (
                  <SelectItem key={idx} value={cName}>
                    {cName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Warehouse Dropdown */}
          <div className="space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
              {t("inventory.warehouse")}
            </span>
            <Select
              value={filters.warehouse}
              onValueChange={(val) => setFilters((prev) => ({ ...prev, warehouse: val }))}
            >
              <SelectTrigger className="h-9 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full">
                <SelectValue placeholder={t("inventory.allWarehouses")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">{t("inventory.allWarehouses")}</SelectItem>
                {filterOptions.warehouses.map((wName, idx) => (
                  <SelectItem key={idx} value={wName}>
                    {wName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Product Dropdown */}
          <div className="space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
              {t("inventory.product")}
            </span>
            <Select
              value={filters.productId}
              onValueChange={(val) => setFilters((prev) => ({ ...prev, productId: val }))}
            >
              <SelectTrigger className="h-9 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full">
                <SelectValue placeholder={t("inventory.allProducts")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">{t("inventory.allProducts")}</SelectItem>
                {filterOptions.products.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Reset filters bar */}
        <div className="flex items-center justify-between border-t border-[var(--color-text-border)]/45 mt-4 pt-3.5">
          <span className="text-[11px] font-semibold text-[var(--color-text-secondary)]">
            {t("inventory.activeFilters")} <strong className="text-foreground font-mono">{activeFiltersCount}</strong>
          </span>
          <button
            type="button"
            onClick={handleResetFilters}
            disabled={activeFiltersCount === 0}
            className="inline-flex items-center gap-1.5 h-8.5 px-3 rounded-lg border border-[var(--color-text-border)] bg-white text-xs font-bold text-foreground hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
          >
            <RefreshCw className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" />
            <span>{t("formActions.resetFilters")}</span>
          </button>
        </div>
      </ContentCard>

      {/* Main Alert List Table */}
      <ContentCard bodyClassName="overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-4 animate-pulse">
            <div className="h-10 bg-gray-100 rounded w-full" />
            <div className="space-y-2">
              {[1, 2, 3].map((idx) => (
                <div key={idx} className="h-12 bg-gray-50/50 rounded w-full" />
              ))}
            </div>
          </div>
        ) : alerts.length === 0 ? (
          /* Empty state */
          <div className="p-12 text-center space-y-4 max-w-sm mx-auto font-sans">
            <div className="h-12 w-12 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center mx-auto shadow-sm">
              <CheckCircle className="h-6 w-6" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-foreground">{t("inventory.noReorderAlerts")}</h4>
              <p className="text-xs text-[var(--color-text-secondary)] mt-1.5 leading-relaxed">
                {t("inventory.noReorderAlertsHint")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/inventory-management/stock-position")}
              className="inline-flex items-center justify-center h-10 px-5 rounded-xl bg-[var(--color-primary)] text-white font-bold text-xs hover:bg-[var(--color-accent)] transition cursor-pointer"
            >
              {t("inventory.returnToStockPosition")}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-fixed min-w-[1410px] border-collapse font-sans">
                <colgroup>
                  <col className="w-[300px]" /> {/* Product */}
                  <col className="w-[180px]" /> {/* Warehouse */}
                  <col className="w-[150px]" /> {/* Category */}
                  <col className="w-[110px]" /> {/* Available Stock */}
                  <col className="w-[110px]" /> {/* Reorder Qty */}
                  <col className="w-[100px]" /> {/* Below By */}
                  <col className="w-[180px]" /> {/* Status */}
                  <col className="w-[280px]" /> {/* Actions */}
                </colgroup>
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)] bg-[var(--color-workspace)]/30 border-b border-[var(--color-text-border)]/65 font-bold">
                    <th className="px-4 py-3 text-left">{t("inventory.product")}</th>
                    <th className="px-4 py-3 text-left">{t("inventory.warehouse")}</th>
                    <th className="px-4 py-3 text-left">{t("common.category")}</th>
                    <th className="px-4 py-3 text-right">{t("status.available")}</th>
                    <th className="px-4 py-3 text-right">{t("inventory.reorderQty")}</th>
                    <th className="px-4 py-3 text-right">{t("inventory.belowBy")}</th>
                    <th className="px-4 py-3 text-left">{t("status.status")}</th>
                    <th className="px-4 py-3 text-right">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 font-medium">
                  {paginatedAlerts.map((row) => {
                    const shortage = Math.max(0, row.reorderQuantity - row.availableStock);
                    return (
                      <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                        {/* Product */}
                        <td className="px-4 py-3">
                          <span className="font-bold text-foreground block leading-snug">
                            {row.productName}
                          </span>
                          <span className="text-[10px] text-gray-400 font-semibold block mt-0.5 font-mono">
                            {row.partNumber}
                          </span>
                        </td>

                        {/* Warehouse */}
                        <td className="px-4 py-3 text-[var(--color-text-secondary)] truncate">
                          {row.warehouse}
                        </td>

                        {/* Category */}
                        <td className="px-4 py-3 text-[var(--color-text-secondary)] truncate">
                          {row.category}
                        </td>

                        {/* Available */}
                        <td className={cn(
                          "px-4 py-3 text-right font-mono font-bold text-sm",
                          row.availableStock === 0 ? "text-rose-600 animate-pulse font-extrabold" : "text-foreground"
                        )}>
                          {row.availableStock}
                        </td>

                        {/* Reorder Threshold */}
                        <td className="px-4 py-3 text-right font-mono text-gray-500">
                          {row.reorderQuantity}
                        </td>

                        {/* Shortage below by */}
                        <td className={cn(
                          "px-4 py-3 text-right font-mono font-extrabold text-sm",
                          shortage >= 100 ? "text-rose-600" : "text-amber-600"
                        )}>
                          {shortage}
                        </td>

                        {/* Status Badge */}
                        <td className="px-4 py-3 text-left">
                          <div className="inline-flex flex-col items-start gap-1">
                            <span className={cn(
                              "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border",
                              getStatusBadgeClass(row.status)
                            )}>
                              {ALERT_STATUS_KEYS[row.status] ? t(ALERT_STATUS_KEYS[row.status]) : row.status}
                            </span>
                            {row.status === "Snoozed" && row.snoozeUntil && (
                              <span className="text-[10px] text-gray-400 font-semibold leading-none">
                                {t("dateTime.untilDate", { date: formatDate(row.snoozeUntil) })}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Actions buttons */}
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => triggerSnooze(row)}
                              className="h-9 px-5 rounded-xl border border-[var(--color-text-border)] text-[var(--color-text-secondary)] font-bold text-xs sm:text-sm hover:bg-gray-50 transition cursor-pointer"
                            >
                              {t("formActions.snooze")}
                            </button>
                            <button
                              type="button"
                              onClick={() => triggerAcknowledge(row)}
                              className="h-9 px-6 rounded-xl bg-[var(--color-primary)] hover:bg-[var(--color-accent)] text-white font-bold text-xs sm:text-sm shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] transition cursor-pointer"
                            >
                              {t("inventory.acknowledge")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-4 pb-4">
              <TablePagination
                page={page}
                totalPages={totalPages}
                totalItems={alerts.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            </div>
          </div>
        )}
      </ContentCard>
    </AppLayout>
  );
}
